// src/core/plugins/moment-jalali.js
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import relativeTime from "dayjs/plugin/relativeTime";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import duration from "dayjs/plugin/duration";
import utc from "dayjs/plugin/utc";
import { toJalaali, toGregorian, isValidJalaaliDate } from "jalaali-js";

// extend dayjs with useful plugins
dayjs.extend(customParseFormat);
dayjs.extend(relativeTime);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(duration);
dayjs.extend(utc);

// Persian month & weekday names
const PERSIAN_MONTHS = [
  "فروردین", "اردیبهشت", "خرداد", "تیر", "مرداد", "شهریور",
  "مهر", "آبان", "آذر", "دی", "بهمن", "اسفند"
];

const PERSIAN_WEEKDAYS = [
  "یکشنبه", "دوشنبه", "سه‌شنبه", "چهارشنبه", "پنج‌شنبه", "جمعه", "شنبه"
];

function pad(n, len = 2) {
  if (n === null || n === undefined) return '00';
  return String(n).padStart(len, "0");
}

// بررسی valid بودن date object
// function isValidDate(date) {
//   if (!date) return false;
//   if (date instanceof Date && isNaN(date.getTime())) return false;
//   if (dayjs(date).isValid() === false) return false;
//   return true;
// }

// تشخیص اینکه آیا رشته ورودی تاریخ شمسی هست یا نه
function isGregorianDateString(input) {
  if (typeof input !== 'string') return false;
  
  const trimmedInput = input.trim();
  
  const gregorianPatterns = [
    /^\d{4}-\d{1,2}-\d{1,2}$/,                   // 2023-12-25
    /^\d{4}\/\d{1,2}\/\d{1,2}$/,                 // 2023/12/25
    /^\d{1,2}-\d{1,2}-\d{4}$/,                   // 12-25-2023
    /^\d{1,2}\/\d{1,2}\/\d{4}$/,                 // 12/25/2023
    /^\d{4}-\d{1,2}-\d{1,2} \d{1,2}:\d{1,2}:\d{1,2}$/, // 2023-12-25 10:30:45
    /^\d{4}\/\d{1,2}\/\d{1,2} \d{1,2}:\d{1,2}:\d{1,2}$/, // 2023/12/25 10:30:45
  ];
  
  const isMatch = gregorianPatterns.some(pattern => pattern.test(trimmedInput));
  
  if (isMatch) {
    const yearMatch = trimmedInput.match(/^(\d{4})/);
    if (yearMatch) {
      const year = parseInt(yearMatch[1]);
      // سال میلادی معمولاً بزرگ است (1900 به بعد)
      const isValidGregorianYear = year >= 1900;
      return isValidGregorianYear;
    }
  }
  
  return false;
}

function isJalaliDateString(input) {
  if (typeof input !== 'string') return false;
  
  const trimmedInput = input.trim();
  if (!trimmedInput) return false;
  
  const jalaliPatterns = [
    /^\d{6}$/,                    // 139907
    /^\d{8}$/,                    // 13990715
    /^\d{4}\/\d{1,2}$/,           // 1398/12
    /^\d{4}\/\d{1,2}\/\d{1,2}$/,  // 1402/10/15
    /^\d{4}-\d{1,2}-\d{1,2}$/,    // 1402-10-15
    /^\d{4}\.\d{1,2}\.\d{1,2}$/,  // 1402.10.15
    /^\d{1,2}\/\d{1,2}\/\d{4}$/,  // 15/10/1402
    /^\d{4}-\d{1,2}-\d{1,2} \d{1,2}:\d{1,2}:\d{1,2}$/,
    /^\d{4}\/\d{1,2}\/\d{1,2} \d{1,2}:\d{1,2}:\d{1,2}$/,
    /^\d{4}-\d{1,2}-\d{1,2} \d{1,2}:\d{1,2}$/,
    /^\d{4}\/\d{1,2}\/\d{1,2} \d{1,2}:\d{1,2}$/,
  ];
  
  const isMatch = jalaliPatterns.some(pattern => pattern.test(trimmedInput));
  
  if (isMatch) {
    const yearMatch = trimmedInput.match(/^(\d{4})/);
    if (yearMatch) {
      const year = parseInt(yearMatch[1]);
      // سال شمسی معمولاً کوچک است (کمتر از 1500)
      const isValidJalaliYear = year < 1500;
      return isValidJalaliYear;
    }
  }
  
  return false;
}

function parseGregorianDate(input) {
  if (typeof input === 'string' && isGregorianDateString(input)) {
    return new Date(input);
  }
  return null;
}

// تبدیل رشته تاریخ شمسی به Date object
function parseJalaliDate(input) {
  if (!isJalaliDateString(input)) return null;
  
  const trimmedInput = input.trim();
  let year, month, day = 1, hours = 0, minutes = 0, seconds = 0;
    
  // جدا کردن تاریخ و زمان
  const [datePart, timePart] = trimmedInput.split(' ');
  
  // پردازش بخش تاریخ
  if (datePart.includes('/')) {
    const parts = datePart.split('/');
    if (parts[0].length === 4) {
      // فرمت 1391/06
      [year, month, day] = parts.map(Number);
      // اگر فقط سال و ماه داریم، روز = 1
      if (parts.length === 2) day = 1;
    } else {
      // فرمت 15/06/1391
      [day, month, year] = parts.map(Number);
    }
  } else if (datePart.includes('-')) {
    [year, month, day] = datePart.split('-').map(Number);
  } 
  else if (datePart.includes('.')) {
    [year, month, day] = datePart.split('.').map(Number);
  } else if (datePart.length === 6) {
    // فرمت 139708
    year = parseInt(datePart.slice(0, 4), 10);
    month = parseInt(datePart.slice(4, 6), 10);
    day = 1;
  }
  
  if (timePart) {
    const timeParts = timePart.split(':');
    hours = Number(timeParts[0]) || 0;
    minutes = Number(timeParts[1]) || 0;
    seconds = Number(timeParts[2]) || 0;
  }

  // اعتبارسنجی
  if (!isValidJalaaliDate(year, month, day)) {
    return null;
  }
    
  // تبدیل شمسی به میلادی
  const gregorian = toGregorian(year, month, day);  
  return new Date(
    gregorian.gy, 
    gregorian.gm - 1, 
    gregorian.gd,
    hours,
    minutes,
    seconds
  );
}

// فاکشن که فرمت‌های حاوی jXXX رو با مقادیر جلالی جایگزین می‌کنه
function formatJalali(dateObj, fmt) {
  try {
    const d = dayjs(dateObj);
    
    if (!d.isValid()) {
      return 'تاریخ نامعتبر';
    }
    
    const { jy, jm, jd } = toJalaali(d.year(), d.month() + 1, d.date());
    const weekday = PERSIAN_WEEKDAYS[d.day()];

    const replacements = {
      "jYYYY": String(jy),
      "jYY": String(jy).slice(-2),
      "jMMMM": PERSIAN_MONTHS[jm - 1],
      "jMM": pad(jm, 2),
      "jM": String(jm),
      "jDD": pad(jd, 2),
      "jD": String(jd),
      "dddd": weekday,
      "HH": pad(d.hour(), 2),      // اینجا از dayjs استفاده میشه
      "H": String(d.hour()),
      "hh": pad(d.hour() % 12 || 12, 2),
      "h": String(d.hour() % 12 || 12),
      "mm": pad(d.minute(), 2),    // اینجا از dayjs استفاده میشه
      "m": String(d.minute()),
      "ss": pad(d.second(), 2),    // اینجا از dayjs استفاده میشه
      "s": String(d.second()),
      "A": d.hour() < 12 ? 'ق.ظ' : 'ب.ظ',
      "a": d.hour() < 12 ? 'ق.ظ' : 'ب.ظ',
    };

    return Object.keys(replacements)
      .sort((a, b) => b.length - a.length)
      .reduce((acc, key) => acc.split(key).join(replacements[key]), fmt);
  } catch (error) {
    return 'خطا در تاریخ';
  }
}

function normalizeUnit(u = "") {
  if (!u) return u;
  return u.endsWith("s") ? u.slice(0, -1) : u;
}

function momentLike(input, format) {
  let d;
  
  // اگر input undefined بود، تاریخ حال
  if (input === undefined) {
    d = dayjs();
  } 
  // اگر تاریخ شمسی بود، parse کن
  else if (typeof input === 'string' && isJalaliDateString(input)) {
    const parsedDate = parseJalaliDate(input);
    if (parsedDate) {
      d = dayjs(parsedDate);
    } else {
      d = dayjs(input, format); // fallback به parse عادی
    }
  }
  // اگر تاریخ میلادی بود، parse کن
  else if (typeof input === 'string' && isGregorianDateString(input)) {
    const parsedDate = parseGregorianDate(input);
    if (parsedDate) {
      d = dayjs(parsedDate);
    } else {
      d = dayjs(input, format);
    }
  }
  // اگر Date object بود
  else if (input instanceof Date) {
    d = dayjs(input);
  }
  // اگر عدد بود (timestamp)
  else if (typeof input === 'number') {
    d = dayjs(input);
  }
  // در غیر این صورت از parse عادی dayjs استفاده کن
  else {
    d = format ? dayjs(input, format) : dayjs(input);
  }

  // اگر تاریخ invalid بود، یک تاریخ پیش‌فرض برگردون
  if (!d.isValid()) {
    d = dayjs(); // تاریخ حال به عنوان fallback
  }

  const wrapper = {
    format(fmt = "YYYY-MM-DD") {
      if (!fmt) return d.toDate();
      
      try {
        if (fmt.includes("j")) {
          return formatJalali(d, fmt);
        }
        return d.format(fmt);
      } catch (error) {
        return 'خطا در فرمت تاریخ';
      }
    },

    add(amount, unit = "day") {
      unit = normalizeUnit(unit);
      return momentLike(d.add(amount, unit));
    },

    subtract(amount, unit = "day") {
      unit = normalizeUnit(unit);
      return momentLike(d.subtract(amount, unit));
    },

    diff(other, unit = "day") {
      try {
        unit = normalizeUnit(unit);
        return d.diff(dayjs(other), unit);
      } catch (error) {
        return NaN;
      }
    },

    isBefore(other) {
      try {
        return d.isBefore(dayjs(other));
      } catch (error) {
        return false;
      }
    },

    isAfter(other) {
      try {
        return d.isAfter(dayjs(other));
      } catch (error) {
        return false;
      }
    },
    
    isSame(other, unit = "day") {
      try {
        unit = normalizeUnit(unit);
        return d.isSame(dayjs(other), unit);
      } catch (error) {
        return false;
      }
    },

    fromNow() {
      try {
        return d.fromNow();
      } catch (error) {
        return '';
      }
    },

    toDate() {
      return d.toDate();
    },
    
    isValid() {
      return d.isValid();
    },
    
    // متدهای جدید برای کار با تاریخ شمسی
    jalaali() {
      try {
        const { jy, jm, jd } = toJalaali(d.year(), d.month() + 1, d.date());
        return { jy, jm, jd };
      } catch (error) {
        return { jy: 1400, jm: 1, jd: 1 }; // مقادیر پیش‌فرض
      }
    },
    
    // تبدیل به رشته تاریخ شمسی
    formatJalaali(format = "jYYYY/jMM/jDD") {
      return formatJalali(d, format);
    },

    // دسترسی به dayjs instance در صورت نیاز
    _dayjs() {
      return d;
    },
  };

  return wrapper;
}

momentLike.duration = function(input, unit) {
  try {
    if (unit) {
      return dayjs.duration(input, unit);
    }
    return dayjs.duration(input);
  } catch (error) {
    return dayjs.duration(0);
  }
};

momentLike.utc = function(input, format) {
  try {
    let d;
    
    if (input === undefined) {
      d = dayjs.utc();
    } else {
      d = format ? dayjs.utc(input, format) : dayjs.utc(input);
    }

    if (!d.isValid()) {
      d = dayjs.utc();
    }

    return momentLike(d);
  } catch (error) {
    return momentLike(dayjs.utc());
  }
};

// اضافه کردن متدهای استاتیک
momentLike.now = function() {
  return momentLike();
};

momentLike.isJalali = function(input) {
  return isJalaliDateString(input);
};

// پلاگین Vue
export default {
  install(app) {
    app.config.globalProperties.$moment = momentLike;
  },
};